import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import { Box, ButtonBase, Dialog, IconButton, Typography } from '@mui/material';
import React from 'react';
import { INTER, NAVY, SLATE, SURFACE_SOFT } from '../studentStyles';

// Full-size view of a picture (legacy image lightbox). The emoji stands in for an
// authored image in the mockup.
export function PictureDialog({ open, onClose, emoji, alt }: { open: boolean; onClose: () => void; emoji: string; alt: string }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-label={alt}>
            <Box sx={{ position: 'relative', p: 3, textAlign: 'center' }}>
                <IconButton aria-label="Close picture" onClick={onClose} sx={{ width: 44, height: 44, position: 'absolute', right: 8, top: 8, color: SLATE }}>
                    <CloseRoundedIcon />
                </IconButton>
                <Box sx={{ height: { xs: 260, sm: 360 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography role="img" aria-label={alt} sx={{ fontSize: { xs: 180, sm: 240 }, lineHeight: 1 }}>{emoji}</Typography>
                </Box>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: NAVY }}>{alt}</Typography>
            </Box>
        </Dialog>
    );
}

// Small corner button that opens the full-size view, for pictures that are also
// answer choices (the tile itself answers the question).
export function ZoomButton({ emoji, alt }: { emoji: string; alt: string }) {
    const [open, setOpen] = React.useState(false);
    return (
        <>
            <IconButton
                aria-label={`Enlarge picture: ${alt}`}
                onClick={() => setOpen(true)}
                size="small"
                sx={{ position: 'absolute', right: 6, top: 6, width: 44, height: 44, backgroundColor: 'rgba(255,255,255,0.9)', '&:hover': { backgroundColor: '#fff' } }}
            >
                <ZoomInRoundedIcon sx={{ fontSize: 18, color: SLATE }} />
            </IconButton>
            <PictureDialog open={open} onClose={() => setOpen(false)} emoji={emoji} alt={alt} />
        </>
    );
}

interface ImageZoomProps {
    emoji: string;
    alt: string;
    height: number;
    fontSize: number;
    rounded?: boolean;
}

// A picture the student can tap to see full size.
export default function ImageZoom({ emoji, alt, height, fontSize, rounded = false }: ImageZoomProps) {
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <ButtonBase
                aria-label={`Enlarge picture: ${alt}`}
                onClick={() => setOpen(true)}
                sx={{
                    position: 'relative', width: '100%', height, backgroundColor: SURFACE_SOFT,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: rounded ? '24px' : 0,
                }}
            >
                <Typography role="img" aria-label={alt} sx={{ fontSize, lineHeight: 1 }}>{emoji}</Typography>
                <Box sx={{
                    position: 'absolute', right: 10, bottom: 10, width: 32, height: 32, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)',
                }}>
                    <ZoomInRoundedIcon sx={{ fontSize: 20, color: SLATE }} />
                </Box>
            </ButtonBase>
            <PictureDialog open={open} onClose={() => setOpen(false)} emoji={emoji} alt={alt} />
        </>
    );
}
